import { HttpClient } from '../api/http-client';
import { OPERATIONS_SERVICE } from '../api/services-path';
import {
  asBoolean,
  asString,
  extractArray,
  extractRecord,
} from './marketingApi.utils';

export type TenantStatus = 'DRAFT' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
export type AdminUserStatus = 'INVITED' | 'ACTIVE' | 'SUSPENDED';
export type WrapperType =
  'GIA' | 'ISA' | 'JISA' | 'SIPP' | 'ALTERNATIVE_INVESTMENTS';
export type FeeType = 'AD_VALOREM' | 'TIERED' | 'FIXED';
export type FeeDefinitionStatus = 'DRAFT' | 'ACTIVE' | 'RETIRED';
export type CapFrequency = 'MONTHLY' | 'ANNUALLY';
export type BroadcastNoticeSeverity =
  'INFO' | 'WARNING' | 'CRITICAL' | 'SUCCESS';
export type BroadcastNoticeStatus = 'DRAFT' | 'ACTIVE' | 'RETIRED';
export type BroadcastNoticeSurface = 'DASHBOARD' | 'ALL';

export interface PlatformAddress {
  line1: string;
  line2: string;
  city: string;
  county: string;
  postcode: string;
  countryCode: string;
}

export interface PlatformTenantContact {
  name: string;
  email: string;
  phone: string;
  website: string;
}

export interface PlatformTenantBankDetails {
  sortCode: string;
  accountNumber: string;
  accountName: string;
}

export interface PlatformRegulatorySettings {
  clientServicingEnabled: boolean;
  clientCommunicationsEnabled: boolean;
  vulnerableCustomerSupportEnabled: boolean;
  regulatoryReportingEnabled: boolean;
  complaintsHandlingEnabled: boolean;
  autoSellForFeesEnabled: boolean;
}

export interface PlatformWrapperConfig {
  id?: string;
  wrapperType: WrapperType;
  enabled: boolean;
  autoSellForFeesMandatory: boolean;
  externalFeePaymentAllowed: boolean;
  userNotificationRequired: boolean;
  notes: string;
}

export interface PlatformAdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: AdminUserStatus;
  externalSubject: string;
  cognitoUsername: string;
  cognitoSubject: string;
}

export interface PlatformTenant {
  id: string;
  tenantCode: string;
  name: string;
  tradingName: string;
  baseUrl: string;
  status: TenantStatus;
  wealthPlatformEnabled: boolean;
  wrappers: PlatformWrapperConfig[];
  address: PlatformAddress;
  contact: PlatformTenantContact;
  bankAccount: PlatformTenantBankDetails;
  adminUsers: PlatformAdminUser[];
  createdAt: string;
  updatedAt: string;
}

export interface PlatformAdminUserInput {
  email: string;
  username?: string;
  firstName: string;
  lastName: string;
  externalSubject?: string;
  temporaryPassword?: string;
  sendInvitation?: boolean;
}

export interface PlatformAdminUserUpdateInput extends PlatformAdminUserInput {
  status: AdminUserStatus;
}

export interface PlatformTenantInput {
  tenantCode?: string;
  name: string;
  tradingName?: string;
  baseUrl?: string;
  status?: TenantStatus;
  wealthPlatformEnabled?: boolean;
  wrappers?: PlatformWrapperConfig[];
  address: PlatformAddress;
  contact?: PlatformTenantContact;
  bankAccount?: PlatformTenantBankDetails;
  adminUser?: PlatformAdminUserInput;
}

export interface TenantOperationsConfiguration {
  tenantId: string;
  wealthPlatformEnabled: boolean;
  wrappers: PlatformWrapperConfig[];
  regulatorySettings: PlatformRegulatorySettings;
  createdAt: string;
  updatedAt: string;
}

const normalizeWealthPlatformEnabled = (value: unknown): boolean =>
  value === false ? false : value == null ? true : asBoolean(value);

export interface PlatformFeeTier {
  id?: string;
  lowerLimit: string;
  upperLimit: string;
  basisPoints: string;
  tierOrder?: number;
}

export interface PlatformFeeDefinition {
  id: string;
  tenantId: string;
  name: string;
  feeType: FeeType;
  basisPoints: string;
  fixedAmount: string;
  hasCap: boolean;
  capAmount: string;
  capFrequency: CapFrequency | '';
  status: FeeDefinitionStatus;
  effectiveFrom: string;
  effectiveTo: string;
  appliesToAllWrappers: boolean;
  wrapperTypes: WrapperType[];
  tiers: PlatformFeeTier[];
  createdAt: string;
  updatedAt: string;
}

export interface PlatformFeeDefinitionInput {
  name: string;
  feeType: FeeType;
  basisPoints?: string;
  fixedAmount?: string;
  hasCap: boolean;
  capAmount?: string;
  capFrequency?: CapFrequency | '';
  effectiveFrom: string;
  effectiveTo?: string;
  appliesToAllWrappers: boolean;
  wrapperTypes: WrapperType[];
  tiers: PlatformFeeTier[];
}

export interface PlatformBroadcastNotice {
  id: string;
  tenantId: string;
  title: string;
  message: string;
  severity: BroadcastNoticeSeverity;
  surface: BroadcastNoticeSurface;
  status: BroadcastNoticeStatus;
  dismissible: boolean;
  startsAt: string;
  endsAt: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformBroadcastNoticeInput {
  title: string;
  message: string;
  severity: BroadcastNoticeSeverity;
  surface: BroadcastNoticeSurface;
  dismissible: boolean;
  startsAt?: string;
  endsAt?: string;
  actorId?: string;
}

export const wrapperTypes: WrapperType[] = [
  'GIA',
  'ISA',
  'JISA',
  'SIPP',
  'ALTERNATIVE_INVESTMENTS',
];

const PLATFORM_TENANTS_PATH = '/api/v1/tenant/platform';
const OPERATIONS_TENANT_FEES_PATH = `${OPERATIONS_SERVICE}/tenants`;

const asEnum = <T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T => {
  const candidate = asString(value) as T;
  return allowed.includes(candidate) ? candidate : fallback;
};

const asDecimalString = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return asString(value);
};

const normalizeAddress = (value: unknown): PlatformAddress => {
  const record = extractRecord(value) ?? {};
  return {
    line1: asString(record.line1),
    line2: asString(record.line2),
    city: asString(record.city),
    county: asString(record.county),
    postcode: asString(record.postcode),
    countryCode: asString(record.countryCode) || 'GB',
  };
};

const normalizeTenantContact = (value: unknown): PlatformTenantContact => {
  const record = extractRecord(value) ?? {};
  return {
    name: asString(
      record.name ?? record.contactName ?? record.primaryContactName,
    ),
    email: asString(record.email ?? record.contactEmail),
    phone: asString(record.phone ?? record.contactPhone),
    website: asString(record.website ?? record.websiteUrl),
  };
};

const normalizeRegulatorySettings = (
  value: unknown,
): PlatformRegulatorySettings => {
  const record = extractRecord(value) ?? {};
  return {
    clientServicingEnabled: asBoolean(record.clientServicingEnabled),
    clientCommunicationsEnabled: asBoolean(record.clientCommunicationsEnabled),
    vulnerableCustomerSupportEnabled: asBoolean(
      record.vulnerableCustomerSupportEnabled,
    ),
    regulatoryReportingEnabled: asBoolean(record.regulatoryReportingEnabled),
    complaintsHandlingEnabled: asBoolean(record.complaintsHandlingEnabled),
    autoSellForFeesEnabled: asBoolean(record.autoSellForFeesEnabled),
  };
};

const normalizeWrapper = (value: unknown): PlatformWrapperConfig => {
  const record = extractRecord(value) ?? {};
  return {
    id: asString(record.id),
    wrapperType: asEnum(record.wrapperType, wrapperTypes, 'GIA'),
    enabled: asBoolean(record.enabled),
    autoSellForFeesMandatory: asBoolean(record.autoSellForFeesMandatory),
    externalFeePaymentAllowed: asBoolean(record.externalFeePaymentAllowed),
    userNotificationRequired: asBoolean(record.userNotificationRequired),
    notes: asString(record.notes),
  };
};

const normalizeAdminUser = (value: unknown): PlatformAdminUser => {
  const record = extractRecord(value) ?? {};
  return {
    id: asString(record.id),
    email: asString(record.email),
    firstName: asString(record.firstName),
    lastName: asString(record.lastName),
    status: asEnum(
      record.status,
      ['INVITED', 'ACTIVE', 'SUSPENDED'] as const,
      'INVITED',
    ),
    externalSubject: asString(record.externalSubject),
    cognitoUsername: asString(record.cognitoUsername),
    cognitoSubject: asString(record.cognitoSubject),
  };
};

const normalizeTenant = (value: unknown): PlatformTenant => {
  const record = extractRecord(value) ?? {};
  return {
    id: asString(record.id),
    tenantCode: asString(record.tenantCode),
    name: asString(record.name),
    tradingName: asString(record.tradingName),
    baseUrl: asString(record.baseUrl ?? record.portalBaseUrl ?? record.baseUrl),
    status: asEnum(
      record.status,
      ['DRAFT', 'ACTIVE', 'SUSPENDED', 'ARCHIVED'] as const,
      'DRAFT',
    ),
    wealthPlatformEnabled: normalizeWealthPlatformEnabled(
      record.wealthPlatformEnabled,
    ),
    wrappers: extractArray(record.wrappers).map(normalizeWrapper),
    address: normalizeAddress(record.address),
    contact: normalizeTenantContact(
      record.contact ?? record.contactDetails ?? record,
    ),
    bankAccount: normalizeTenantBankDetails(
      record.bankAccount ?? record.bankDetails ?? record.bank,
    ),
    adminUsers: extractArray(record.adminUsers).map(normalizeAdminUser),
    createdAt: asString(record.createdAt),
    updatedAt: asString(record.updatedAt),
  };
};

const normalizeTenantBankDetails = (
  value: unknown,
): PlatformTenantBankDetails => {
  const record = extractRecord(value) ?? {};
  return {
    sortCode: asString(record.sortCode),
    accountNumber: asString(record.accountNumber),
    accountName: asString(record.accountName ?? record.name),
  };
};

const normalizeFeeTier = (value: unknown): PlatformFeeTier => {
  const record = extractRecord(value) ?? {};
  return {
    id: asString(record.id),
    lowerLimit: asDecimalString(record.lowerLimit),
    upperLimit: asDecimalString(record.upperLimit),
    basisPoints: asDecimalString(record.basisPoints),
    tierOrder:
      typeof record.tierOrder === 'number' && Number.isFinite(record.tierOrder)
        ? record.tierOrder
        : undefined,
  };
};

const normalizeFeeDefinition = (value: unknown): PlatformFeeDefinition => {
  const record = extractRecord(value) ?? {};
  return {
    id: asString(record.id),
    tenantId: asString(record.tenantId),
    name: asString(record.name),
    feeType: asEnum(
      record.feeType,
      ['AD_VALOREM', 'TIERED', 'FIXED'] as const,
      'AD_VALOREM',
    ),
    basisPoints: asDecimalString(record.basisPoints),
    fixedAmount: asDecimalString(record.fixedAmount),
    hasCap: asBoolean(record.hasCap),
    capAmount: asDecimalString(record.capAmount),
    capFrequency: asEnum(
      record.capFrequency,
      ['MONTHLY', 'ANNUALLY', ''] as const,
      '',
    ),
    status: asEnum(
      record.status,
      ['DRAFT', 'ACTIVE', 'RETIRED'] as const,
      'DRAFT',
    ),
    effectiveFrom: asString(record.effectiveFrom),
    effectiveTo: asString(record.effectiveTo),
    appliesToAllWrappers: asBoolean(record.appliesToAllWrappers),
    wrapperTypes: extractArray(record.wrapperTypes).map((wrapperType) =>
      asEnum(wrapperType, wrapperTypes, 'GIA'),
    ),
    tiers: extractArray(record.tiers).map(normalizeFeeTier),
    createdAt: asString(record.createdAt),
    updatedAt: asString(record.updatedAt),
  };
};

const normalizeBroadcastNotice = (value: unknown): PlatformBroadcastNotice => {
  const record = extractRecord(value) ?? {};
  return {
    id: asString(record.id),
    tenantId: asString(record.tenantId),
    title: asString(record.title),
    message: asString(record.message),
    severity: asEnum(
      record.severity,
      ['INFO', 'WARNING', 'CRITICAL', 'SUCCESS'] as const,
      'INFO',
    ),
    surface: asEnum(record.surface, ['DASHBOARD', 'ALL'] as const, 'DASHBOARD'),
    status: asEnum(
      record.status,
      ['DRAFT', 'ACTIVE', 'RETIRED'] as const,
      'DRAFT',
    ),
    dismissible: record.dismissible !== false,
    startsAt: asString(record.startsAt),
    endsAt: asString(record.endsAt),
    createdBy: asString(record.createdBy),
    updatedBy: asString(record.updatedBy),
    createdAt: asString(record.createdAt),
    updatedAt: asString(record.updatedAt),
  };
};

const compactCreateTenantPayload = (input: PlatformTenantInput) => ({
  tenantCode: input.tenantCode,
  name: input.name,
  tradingName: input.tradingName,
  baseUrl: input.baseUrl,
  address: input.address,
  contact: input.contact,
  bankAccount: input.bankAccount,
  adminUser: input.adminUser,
});

const compactUpdateTenantPayload = (input: PlatformTenantInput) => ({
  name: input.name,
  tradingName: input.tradingName,
  baseUrl: input.baseUrl,
  status: input.status,
  address: input.address,
  contact: input.contact,
  bankAccount: input.bankAccount,
});

const compactTenantOperationsConfigurationPayload = (
  input: TenantOperationsConfiguration,
) => ({
  wealthPlatformEnabled: input.wealthPlatformEnabled,
  wrappers: input.wrappers,
  regulatorySettings: input.regulatorySettings,
});

const compactFeePayload = (input: PlatformFeeDefinitionInput) => ({
  ...input,
  basisPoints:
    input.feeType === 'AD_VALOREM' ? input.basisPoints || null : null,
  fixedAmount: input.feeType === 'FIXED' ? input.fixedAmount || null : null,
  capAmount: input.hasCap ? input.capAmount || null : null,
  capFrequency: input.hasCap ? input.capFrequency || null : null,
  effectiveTo: input.effectiveTo || null,
  wrapperTypes: input.appliesToAllWrappers ? [] : input.wrapperTypes,
  tiers: input.feeType === 'TIERED' ? input.tiers : [],
});

const compactBroadcastNoticePayload = (
  input: PlatformBroadcastNoticeInput,
) => ({
  title: input.title,
  message: input.message,
  severity: input.severity,
  surface: input.surface,
  dismissible: input.dismissible,
  startsAt: input.startsAt || null,
  endsAt: input.endsAt || null,
  actorId: input.actorId || null,
});

export const listPlatformTenants = async (): Promise<PlatformTenant[]> => {
  const { data } = await HttpClient.get<unknown>(PLATFORM_TENANTS_PATH);
  return extractArray(data).map(normalizeTenant);
};

export const getPlatformTenant = async (
  tenantId: string,
): Promise<PlatformTenant> => {
  const { data } = await HttpClient.get<unknown>(
    `${PLATFORM_TENANTS_PATH}/${tenantId}`,
  );
  return normalizeTenant(data);
};

export const createPlatformTenant = async (
  input: PlatformTenantInput,
): Promise<PlatformTenant> => {
  const { data } = await HttpClient.post<unknown>(
    PLATFORM_TENANTS_PATH,
    compactCreateTenantPayload(input),
  );
  return normalizeTenant(data);
};

export const updatePlatformTenant = async (
  tenantId: string,
  input: PlatformTenantInput,
): Promise<PlatformTenant> => {
  const { data } = await HttpClient.put<unknown>(
    `${PLATFORM_TENANTS_PATH}/${tenantId}`,
    compactUpdateTenantPayload(input),
  );
  return normalizeTenant(data);
};

export const listPlatformAdminUsers = async (
  tenantId: string,
): Promise<PlatformAdminUser[]> => {
  const { data } = await HttpClient.get<unknown>(
    `${PLATFORM_TENANTS_PATH}/${tenantId}/admin-users`,
  );
  return extractArray(data).map(normalizeAdminUser);
};

export const createPlatformAdminUser = async (
  tenantId: string,
  input: PlatformAdminUserInput,
): Promise<PlatformAdminUser> => {
  const { data } = await HttpClient.post<unknown>(
    `${PLATFORM_TENANTS_PATH}/${tenantId}/admin-users`,
    input,
  );
  return normalizeAdminUser(data);
};

export const updatePlatformAdminUser = async (
  tenantId: string,
  adminUserId: string,
  input: PlatformAdminUserUpdateInput,
): Promise<PlatformAdminUser> => {
  const { data } = await HttpClient.put<unknown>(
    `${PLATFORM_TENANTS_PATH}/${tenantId}/admin-users/${adminUserId}`,
    input,
  );
  return normalizeAdminUser(data);
};

const normalizeOperationsConfiguration = (
  data: unknown,
): TenantOperationsConfiguration => {
  const record = extractRecord(data) ?? {};
  if (
    typeof record.wealthPlatformEnabled !== 'boolean' ||
    !Array.isArray(record.wrappers)
  ) {
    throw new Error(
      'The server did not return account type settings. Configuration could not be confirmed.',
    );
  }
  return {
    tenantId: asString(record.tenantId),
    wealthPlatformEnabled: record.wealthPlatformEnabled,
    wrappers: record.wrappers.map(normalizeWrapper),
    regulatorySettings: normalizeRegulatorySettings(record.regulatorySettings),
    createdAt: asString(record.createdAt),
    updatedAt: asString(record.updatedAt),
  };
};

export const isPrivateEquityEnabled = (
  configuration:
    | Pick<TenantOperationsConfiguration, 'wealthPlatformEnabled' | 'wrappers'>
    | undefined,
): boolean =>
  Boolean(
    configuration?.wealthPlatformEnabled &&
    configuration.wrappers.some(
      (wrapper) =>
        wrapper.wrapperType === 'ALTERNATIVE_INVESTMENTS' && wrapper.enabled,
    ),
  );

export const getTenantOperationsConfiguration = async (
  tenantId: string,
): Promise<TenantOperationsConfiguration> => {
  const { data } = await HttpClient.get<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/configuration`,
  );
  return normalizeOperationsConfiguration(data);
};

export const updateTenantOperationsConfiguration = async (
  tenantId: string,
  input: TenantOperationsConfiguration,
): Promise<TenantOperationsConfiguration> => {
  const { data } = await HttpClient.put<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/configuration`,
    compactTenantOperationsConfigurationPayload(input),
  );
  normalizeOperationsConfiguration(data);
  const saved = await getTenantOperationsConfiguration(tenantId);
  const enabledTypes = (wrappers: PlatformWrapperConfig[]) =>
    wrappers
      .filter((wrapper) => wrapper.enabled)
      .map((wrapper) => wrapper.wrapperType)
      .sort()
      .join(',');
  if (
    saved.wealthPlatformEnabled !== input.wealthPlatformEnabled ||
    enabledTypes(saved.wrappers) !== enabledTypes(input.wrappers)
  ) {
    throw new Error(
      'Account type settings were not saved. Please refresh and try again.',
    );
  }
  return saved;
};

export const listPlatformFeeDefinitions = async (
  tenantId: string,
): Promise<PlatformFeeDefinition[]> => {
  const { data } = await HttpClient.get<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/fee-definitions`,
  );
  return extractArray(data).map(normalizeFeeDefinition);
};

export const getPlatformFeeDefinition = async (
  tenantId: string,
  feeDefinitionId: string,
): Promise<PlatformFeeDefinition> => {
  const { data } = await HttpClient.get<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/fee-definitions/${feeDefinitionId}`,
  );
  return normalizeFeeDefinition(data);
};

export const createPlatformFeeDefinition = async (
  tenantId: string,
  input: PlatformFeeDefinitionInput,
): Promise<PlatformFeeDefinition> => {
  const { data } = await HttpClient.post<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/fee-definitions`,
    compactFeePayload(input),
  );
  return normalizeFeeDefinition(data);
};

export const updatePlatformFeeDefinition = async (
  tenantId: string,
  feeDefinitionId: string,
  input: PlatformFeeDefinitionInput,
): Promise<PlatformFeeDefinition> => {
  const { data } = await HttpClient.put<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/fee-definitions/${feeDefinitionId}`,
    compactFeePayload(input),
  );
  return normalizeFeeDefinition(data);
};

export const updatePlatformFeeDefinitionName = async (
  tenantId: string,
  feeDefinitionId: string,
  input: Pick<PlatformFeeDefinitionInput, 'name'>,
): Promise<PlatformFeeDefinition> => {
  const { data } = await HttpClient.patch<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/fee-definitions/${feeDefinitionId}/name`,
    input,
  );
  return normalizeFeeDefinition(data);
};

export const activatePlatformFeeDefinition = async (
  tenantId: string,
  feeDefinitionId: string,
): Promise<PlatformFeeDefinition> => {
  const { data } = await HttpClient.post<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/fee-definitions/${feeDefinitionId}/activate`,
  );
  return normalizeFeeDefinition(data);
};

export const retirePlatformFeeDefinition = async (
  tenantId: string,
  feeDefinitionId: string,
): Promise<PlatformFeeDefinition> => {
  const { data } = await HttpClient.post<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/fee-definitions/${feeDefinitionId}/retire`,
  );
  return normalizeFeeDefinition(data);
};

export const listPlatformBroadcastNotices = async (
  tenantId: string,
): Promise<PlatformBroadcastNotice[]> => {
  const { data } = await HttpClient.get<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/broadcast-notices`,
  );
  return extractArray(data).map(normalizeBroadcastNotice);
};

export const createPlatformBroadcastNotice = async (
  tenantId: string,
  input: PlatformBroadcastNoticeInput,
): Promise<PlatformBroadcastNotice> => {
  const { data } = await HttpClient.post<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/broadcast-notices`,
    compactBroadcastNoticePayload(input),
  );
  return normalizeBroadcastNotice(data);
};

export const updatePlatformBroadcastNotice = async (
  tenantId: string,
  noticeId: string,
  input: PlatformBroadcastNoticeInput,
): Promise<PlatformBroadcastNotice> => {
  const { data } = await HttpClient.put<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/broadcast-notices/${noticeId}`,
    compactBroadcastNoticePayload(input),
  );
  return normalizeBroadcastNotice(data);
};

export const activatePlatformBroadcastNotice = async (
  tenantId: string,
  noticeId: string,
): Promise<PlatformBroadcastNotice> => {
  const { data } = await HttpClient.post<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/broadcast-notices/${noticeId}/activate`,
  );
  return normalizeBroadcastNotice(data);
};

export const retirePlatformBroadcastNotice = async (
  tenantId: string,
  noticeId: string,
): Promise<PlatformBroadcastNotice> => {
  const { data } = await HttpClient.post<unknown>(
    `${OPERATIONS_TENANT_FEES_PATH}/${tenantId}/broadcast-notices/${noticeId}/retire`,
  );
  return normalizeBroadcastNotice(data);
};
